// ============================================================================
// <NavigateButton> — the ONE "open in maps" control (CW-C Phase 1, 2026-08-04).
//
// Replaces two implementations that drifted apart without anyone deciding:
//   * facility-detail.tsx  → Apple Maps, `?q=<address>`  (a SEARCH, not even
//                            directions)
//   * facility-sheet.tsx   → Google Maps, stored lat/long
// They disagreed whenever the stored coordinate was stale — true for 175 of
// 206 non-closed facilities (geocode_precision = NULL).
//
// Provider is remembered per user in localStorage. Detection only picks the
// FIRST-RUN default: navigator.userAgentData is Chromium-only (absent on iOS
// Safari, the exact case we'd want it for) and iPadOS reports itself as macOS,
// so a remembered explicit choice is the only reliable mechanism. Four of six
// field-service products surveyed (Onfleet, Samsara, Track-POD, EasyRoutes)
// ship exactly this; none document UA-based auto-selection.
//
// Route MATH stays on coordinates (route-order.ts / route-links.ts). This is
// single-destination navigation only.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Navigation } from 'lucide-react';
import {
  buildNavigateUrl,
  canNavigate,
  detectDefaultProvider,
  NAV_PROVIDERS,
  NAV_PROVIDER_LABEL,
  type NavProvider,
  type NavTarget,
} from './navigate-link';

const STORAGE_KEY = 'tcs.navProvider';

function readProvider(): NavProvider | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return NAV_PROVIDERS.includes(raw as NavProvider) ? (raw as NavProvider) : null;
  } catch {
    return null;
  }
}

function writeProvider(p: NavProvider): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, p);
  } catch {
    /* private browsing — the default just applies again next time */
  }
}

/** Remembered navigation-provider preference. Exported for reuse/testing. */
export function useNavProvider(): [NavProvider, (p: NavProvider) => void] {
  const [provider, setProvider] = useState<NavProvider>(() => readProvider() ?? 'google');

  // Detection runs in an effect so SSR/tests never touch navigator, and only
  // when the user has made no explicit choice.
  useEffect(() => {
    if (readProvider()) return;
    setProvider(
      detectDefaultProvider(navigator.userAgent, navigator.maxTouchPoints ?? 0),
    );
  }, []);

  const choose = useCallback((p: NavProvider) => {
    setProvider(p);
    writeProvider(p);
  }, []);

  return [provider, choose];
}

export interface NavigateButtonProps {
  target: NavTarget;
  /** 'primary' = filled CTA (map sheet), 'link' = inline text (info tab). */
  variant?: 'primary' | 'link';
  className?: string;
  /** Rendered in place of the button when there is nothing to route to. */
  fallback?: React.ReactNode;
}

/**
 * Single-destination navigation, address-first, with a remembered provider.
 *
 * Renders `fallback` (or nothing) when the target has neither an address nor a
 * coordinate — never a dead button. Ben's rule: a missing address is not
 * acceptable state, so callers pass an actionable prompt as `fallback`.
 */
export function NavigateButton({
  target,
  variant = 'primary',
  className,
  fallback = null,
}: NavigateButtonProps) {
  const [provider, chooseProvider] = useNavProvider();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!canNavigate(target)) return <>{fallback}</>;

  const href = buildNavigateUrl(provider, target);
  if (!href) return <>{fallback}</>;

  const label = target.name ? `Navigate to ${target.name}` : 'Navigate here';

  const linkCls =
    variant === 'primary'
      ? 'inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-lg bg-tcs-inverse px-3 py-2 text-center text-xs font-semibold text-tcs-inverse-ink'
      : 'inline-flex items-center gap-1 text-xs font-medium text-tcs-text underline decoration-tcs-border hover:decoration-tcs-text';

  return (
    <span className={`relative inline-flex items-center gap-1 ${className ?? ''}`}>
      <a href={href} target="_blank" rel="noreferrer" className={linkCls} aria-label={label}>
        <Navigation className="h-3.5 w-3.5" aria-hidden />
        Navigate
      </a>
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={`Navigation app: ${NAV_PROVIDER_LABEL[provider]}. Change.`}
        className="inline-flex min-h-[44px] items-center rounded-md px-1.5 text-[10px] font-medium text-tcs-text-muted hover:text-tcs-text"
      >
        {NAV_PROVIDER_LABEL[provider]} ▾
      </button>
      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 min-w-[9rem] overflow-hidden rounded-lg border border-tcs-border bg-tcs-overlay py-1 shadow-lg"
        >
          {NAV_PROVIDERS.map((p) => (
            <button
              key={p}
              type="button"
              role="menuitemradio"
              aria-checked={p === provider}
              onClick={() => {
                chooseProvider(p);
                setMenuOpen(false);
              }}
              className={`block w-full px-3 py-2 text-left text-xs ${
                p === provider
                  ? 'bg-tcs-bg-muted font-semibold text-tcs-text'
                  : 'text-tcs-text-secondary hover:bg-tcs-bg-muted'
              }`}
            >
              {NAV_PROVIDER_LABEL[p]}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
